import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  AttachMoney,
  Search,
  TrendingUp,
  AccountBalanceWallet,
  AccessTime,
  CheckCircle,
} from '@mui/icons-material';
import { vendorAPI } from 'services/api';

const paymentStatusConfig = {
  completed: { label: 'Completed', color: '#4CAF50', icon: <CheckCircle fontSize="small" /> },
  pending: { label: 'Pending', color: '#FF9800', icon: <AccessTime fontSize="small" /> },
  processing: { label: 'Processing', color: '#2196F3', icon: <AccessTime fontSize="small" /> },
  failed: { label: 'Failed', color: '#F44336', icon: null },
  cancelled: { label: 'Cancelled', color: '#9E9E9E', icon: null },
};

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const loadPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await vendorAPI.getPayments();
      setPayments(response.data || []);
    } catch (err) {
      console.error('Failed to load payments:', err);
      setError('Failed to load payment history.');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  // Summary calculations
  const totalEarnings = payments
    .filter((p) => p.status === 'completed')
    .reduce((sum, p) => sum + (p.amount || 0), 0);
  const pendingAmount = payments
    .filter((p) => ['pending', 'processing'].includes(p.status))
    .reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalTransactions = payments.length;
  const thisMonthPayments = payments.filter((p) => {
    if (!p.payment_date) return false;
    const d = new Date(p.payment_date);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const thisMonthAmount = thisMonthPayments
    .filter((p) => p.status === 'completed')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  // Filtered payments
  const filtered = payments.filter((p) => {
    const matchesSearch =
      !searchTerm ||
      (p.reference_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.order_number || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          💰 Payment History
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track all your payments, earnings, and pending settlements.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary KPIs */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AttachMoney sx={{ mr: 1 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Earnings</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                ₹{totalEarnings.toLocaleString('en-IN')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #FF9800 0%, #EF6C00 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccountBalanceWallet sx={{ mr: 1 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Pending Amount</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                ₹{pendingAmount.toLocaleString('en-IN')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #2196F3 0%, #1565C0 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp sx={{ mr: 1 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>This Month</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                ₹{thisMonthAmount.toLocaleString('en-IN')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircle sx={{ mr: 1 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Transactions</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {totalTransactions}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by reference or order number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Payment Records ({filtered.length})
          </Typography>

          {filtered.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>Reference #</strong></TableCell>
                    <TableCell><strong>Order #</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell><strong>Method</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((payment) => {
                    const config = paymentStatusConfig[payment.status] || {
                      label: payment.status,
                      color: '#757575',
                    };
                    return (
                      <TableRow key={payment.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {payment.reference_number || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>{payment.order_number || '-'}</TableCell>
                        <TableCell>
                          {payment.payment_date
                            ? new Date(payment.payment_date).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            ₹{(payment.amount || 0).toLocaleString('en-IN')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" textTransform="capitalize">
                            {payment.payment_method || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={config.label}
                            size="small"
                            icon={config.icon || undefined}
                            sx={{
                              bgcolor: `${config.color}15`,
                              color: config.color,
                              fontWeight: 'bold',
                              fontSize: '0.7rem',
                              '& .MuiChip-icon': { color: config.color },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <AttachMoney sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No payment records found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your filters.'
                  : 'Payments will appear here once orders are settled.'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentHistory;
